import type { Query } from "@tanstack/react-query";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { useParams } from "react-router-dom";
import { adminLoyaltyPointsKey } from "../customers/loyalty-points/useAdminLoyaltyPoints";
import type { SavedOrder, SavedOrderProduct } from "./orderTypes";
import type { CreateOrUpdateOrderRequest, PaymentDTO } from "./ordersApi";
import {
  cancelOrderProduct,
  deleteOrder,
  getOrder,
  moveOrderProducts,
  restoreOrderProducts,
  updateContactPerson,
  updateOrder,
  updateOrderName,
  updateOrderPayments,
} from "./ordersApi";

interface OrderPaymentsUpdate {
  readonly payments: PaymentDTO[];
}

interface ContactPersonUpdate {
  readonly contactPersonEmail: string;
}

interface OrderNameUpdate {
  readonly orderName: string;
}

const ORDERS_QUERY_KEY = "orders";

export function isListOrdersQuery({ queryKey }: Query) {
  return queryKey.length > 1 && queryKey[0] === ORDERS_QUERY_KEY && typeof queryKey[1] === "object";
}

export function isOrderQuery(orderId: string) {
  return ({ queryKey }: Query) => queryKey.length > 1 && queryKey[0] === ORDERS_QUERY_KEY && queryKey[1] === orderId;
}

export const useOrder = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const { data, isLoading } = useQuery({
    queryKey: [ORDERS_QUERY_KEY, orderId],
    queryFn: ({ signal }) => getOrder(orderId!, signal),
    enabled: orderId !== undefined,
  });
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const deleteOrderMutation = useMutation<void, unknown, { orderId: string }>({
    mutationFn: ({ orderId }) => deleteOrder(orderId),
    onSuccess: async (_, { orderId }) => {
      enqueueSnackbar(`Zamówienie zostało usunięte`, { variant: "success" });
      queryClient.removeQueries({ predicate: isOrderQuery(orderId) });
      await queryClient.invalidateQueries({ predicate: isListOrdersQuery });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się usunąć zamówienia. Powód: ${(error as Error).message}`, { variant: "error" });
    },
  });

  const updateOrderMutation = useMutation<
    SavedOrder,
    unknown,
    {
      orderId: string;
      update: CreateOrUpdateOrderRequest;
    }
  >({
    mutationFn: ({ orderId, update }) => updateOrder(orderId, update),
    onSuccess: async (data, { orderId }) => {
      enqueueSnackbar("Zamówienie zostało zaktualizowane", { variant: "success" });
      await queryClient.invalidateQueries({ predicate: isOrderQuery(orderId) });
      queryClient.setQueryData([ORDERS_QUERY_KEY, orderId], data);
      await queryClient.invalidateQueries({ predicate: isListOrdersQuery });
      await queryClient.invalidateQueries({ queryKey: [adminLoyaltyPointsKey] });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się zaktualizować zamówienia. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  const updateOrderPaymentsMutation = useMutation<
    SavedOrder,
    unknown,
    {
      orderId: string;
      update: OrderPaymentsUpdate;
    }
  >({
    mutationFn: ({ orderId, update }) => updateOrderPayments(orderId, update),
    onSuccess: async (data, { orderId }) => {
      enqueueSnackbar("Wpłaty do zamówienia zostały zaktualizowane", { variant: "success" });
      queryClient.setQueryData([ORDERS_QUERY_KEY, orderId], data);
      await queryClient.invalidateQueries({ predicate: isListOrdersQuery });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się zaktualizować wpłat do zamówienia. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  const cancelOrderProductMutation = useMutation<
    unknown,
    unknown,
    {
      orderId: string;
      productIndexes: number[];
      bookingNumber: string;
      cancelledAt: string;
    }
  >({
    mutationFn: ({ orderId, productIndexes, bookingNumber, cancelledAt }) =>
      cancelOrderProduct(orderId, { productIndexes, bookingNumber, cancelledAt }),
    onSuccess: async (_, { orderId }) => {
      enqueueSnackbar("Produkt został anulowany", { variant: "success" });
      await queryClient.invalidateQueries({ predicate: isOrderQuery(orderId) });
      await queryClient.invalidateQueries({ predicate: isListOrdersQuery });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się anulować produktu. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  const restoreOrderProductMutation = useMutation<
    SavedOrderProduct[],
    unknown,
    {
      orderId: string;
      productIndexes: number[];
      bookingNumber: string;
    }
  >({
    mutationFn: ({ orderId, productIndexes, bookingNumber }) =>
      restoreOrderProducts(orderId, { productIndexes, bookingNumber }),
    onSuccess: async (products, { orderId }) => {
      enqueueSnackbar("Anulowanie produktu zostało cofnięte", { variant: "success" });
      if (products.some((p) => !p.bookingNumber)) {
        enqueueSnackbar(
          "Rezerwacja była już anulowana u dostawcy — produkt wrócił bez numeru rezerwacji i wymaga ponownej rezerwacji.",
          { variant: "warning" },
        );
      }
      await queryClient.invalidateQueries({ predicate: isOrderQuery(orderId) });
      await queryClient.invalidateQueries({ predicate: isListOrdersQuery });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się cofnąć anulowania produktu. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  const moveOrderProductMutation = useMutation<
    unknown,
    unknown,
    {
      orderId: string;
      productIndexes: number[];
      targetOrderId: string;
    }
  >({
    mutationFn: ({ orderId, productIndexes, targetOrderId }) =>
      moveOrderProducts(orderId, { productIndexes, targetOrderId }),
    onSuccess: async (_, { orderId, targetOrderId, productIndexes }) => {
      const count = productIndexes.length;
      enqueueSnackbar(count > 1 ? "Produkty zostały przeniesione" : "Produkt został przeniesiony", {
        variant: "success",
      });
      await queryClient.invalidateQueries({ predicate: isOrderQuery(orderId) });
      await queryClient.invalidateQueries({ predicate: isOrderQuery(targetOrderId) });
      await queryClient.invalidateQueries({ predicate: isListOrdersQuery });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się przenieść produktu. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  const updateContactPersonMutation = useMutation<
    SavedOrder,
    unknown,
    {
      orderId: string;
      update: ContactPersonUpdate;
    }
  >({
    mutationFn: ({ orderId, update }) => updateContactPerson(orderId, update),
    onSuccess: async (data, { orderId }) => {
      enqueueSnackbar("Osoba kontaktowa została zaktualizowana", { variant: "success" });
      queryClient.setQueryData([ORDERS_QUERY_KEY, orderId], data);
      await queryClient.invalidateQueries({ predicate: isListOrdersQuery });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się zaktualizować osoby kontaktowej. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  const updateOrderNameMutation = useMutation<
    SavedOrder,
    unknown,
    {
      orderId: string;
      update: OrderNameUpdate;
    }
  >({
    mutationFn: ({ orderId, update }) => updateOrderName(orderId, update),
    onSuccess: async (data, { orderId }) => {
      enqueueSnackbar("Nazwa zamówienia została zauktalizowana", { variant: "success" });
      queryClient.setQueryData([ORDERS_QUERY_KEY, orderId], data);
      await queryClient.invalidateQueries({ predicate: isListOrdersQuery });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się zaktualizować nazwy zamówienia. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  return {
    order: data,
    isLoading,
    onUpdate: (order: CreateOrUpdateOrderRequest) =>
      updateOrderMutation.mutateAsync({ orderId: orderId!, update: order }),
    onUpdatePayments: (payments: PaymentDTO[]) =>
      updateOrderPaymentsMutation.mutateAsync({
        orderId: orderId!,
        update: { payments },
      }),
    onDelete: () => deleteOrderMutation.mutateAsync({ orderId: orderId! }),
    onCancelProduct: (productIndexes: number[], bookingNumber: string, cancelledAt: string) =>
      cancelOrderProductMutation.mutateAsync({
        orderId: orderId!,
        productIndexes,
        bookingNumber,
        cancelledAt,
      }),
    onRestoreProduct: (productIndexes: number[], bookingNumber: string) =>
      restoreOrderProductMutation.mutateAsync({
        orderId: orderId!,
        productIndexes,
        bookingNumber,
      }),
    onMoveProduct: (productIndexes: number[], targetOrderId: string) =>
      moveOrderProductMutation.mutateAsync({ orderId: orderId!, productIndexes, targetOrderId }),
    onUpdateContactPerson: (contactPersonEmail: string) =>
      updateContactPersonMutation.mutateAsync({ orderId: orderId!, update: { contactPersonEmail } }),
    onUpdateOrderName: (orderName: string) =>
      updateOrderNameMutation.mutateAsync({ orderId: orderId!, update: { orderName } }),
  };
};
